// src/pages/sessions/components/LiveSession/layout/MicrophoneWarningBanner.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useRoomContext } from '@livekit/components-react';
import { ParticipantEvent, RoomEvent } from 'livekit-client';
import { MicrophoneIcon, XMarkIcon } from '@heroicons/react/24/outline';

const MIC_VERIFIED_KEY = 'simpleclass_mic_verified';
const DETECTION_TIMEOUT_MS = 10_000;

/**
 * One-shot warning banner shown 10s after another participant joins
 * if no microphone audio is detected.
 * Never shown if mic was previously verified (pre-join screen or earlier session).
 * Auto-hides when speech is detected. Dismissible manually.
 */
const MicrophoneWarningBanner = ({ roomConnected }) => {
  const { t } = useTranslation();
  const room = useRoomContext();
  const [showWarning, setShowWarning] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const speechDetectedRef = useRef(false);
  const timerRef = useRef(null);
  const hasInitializedRef = useRef(false);
  const timerStartedRef = useRef(false);

  useEffect(() => {
    // Only run once when room connects
    if (!roomConnected || !room?.localParticipant || hasInitializedRef.current) return;
    
    // Clear the verified flag on each new session so the check runs fresh.
    // The old behaviour suppressed the warning forever after one successful session,
    // meaning a broken mic in a later session would go undetected.
    try { localStorage.removeItem(MIC_VERIFIED_KEY); } catch {}

    hasInitializedRef.current = true;
    const localParticipant = room.localParticipant;

    const handleSpeaking = (speaking) => {
      if (speaking && !speechDetectedRef.current) {
        speechDetectedRef.current = true;
        // Mic works — remember for future sessions
        try { localStorage.setItem(MIC_VERIFIED_KEY, 'true'); } catch {}
        setShowWarning(false);
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      }
    };

    const startTimer = () => {
      if (timerStartedRef.current || speechDetectedRef.current) return;
      timerStartedRef.current = true;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (!speechDetectedRef.current) {
          setShowWarning(true);
        }
      }, DETECTION_TIMEOUT_MS);
    };

    localParticipant.on(ParticipantEvent.IsSpeakingChanged, handleSpeaking);

    // Start timer only when there's at least one other participant
    if (room.remoteParticipants.size > 0) {
      startTimer();
    } else {
      room.on(RoomEvent.ParticipantConnected, startTimer);
    }

    return () => {
      localParticipant.off(ParticipantEvent.IsSpeakingChanged, handleSpeaking);
      room.off(RoomEvent.ParticipantConnected, startTimer);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [roomConnected, room]);

  if (!showWarning || isDismissed) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-amber-600 text-white px-4 py-3 shadow-lg animate-in slide-in-from-top duration-300">
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <MicrophoneIcon className="w-6 h-6 flex-shrink-0" />
          <div>
            <p className="font-medium">
              {t('liveSession.micWarning.title', 'No sound detected from your microphone')}
            </p>
            <p className="text-sm opacity-90">
              {t('liveSession.micWarning.description', "If you're on a school computer, the microphone may be blocked by your school's settings. Say something to dismiss this warning.")}
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsDismissed(true)}
          className="p-1.5 hover:bg-white/20 rounded-full transition-colors flex-shrink-0"
          aria-label={t('common.dismiss', 'Dismiss')}
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default MicrophoneWarningBanner;
